/**
 * G2 / G10 — hub empty-cart location.state intent (isolated from shop openCartIntent).
 * Frozen keys only: `{ openHubEmptyCart: true }`. Never openCart / openCartSource.
 */

export interface HubEmptyCartLocationState {
  readonly openHubEmptyCart: true;
}

export const HEADER_HUB_EMPTY_CART_STATE: HubEmptyCartLocationState = {
  openHubEmptyCart: true,
};

export type HubEmptyCartConsumeAction = 'noop' | 'open_and_clear' | 'clear_only';

/** Strip openHubEmptyCart; empty remainder → null. */
export function stripHubEmptyCartIntent(state: unknown): unknown {
  if (state === null || typeof state !== 'object') {
    return null;
  }
  const record = state as Record<string, unknown>;
  if (!('openHubEmptyCart' in record)) {
    return state;
  }
  const { openHubEmptyCart: _openHubEmptyCart, ...rest } = record;
  return Object.keys(rest).length === 0 ? null : rest;
}

/**
 * Consume hub empty-cart location.state.
 * - openHubEmptyCart true → open_and_clear
 * - key present but not true → clear_only (fail-closed)
 * - otherwise → noop
 */
export function resolveHubEmptyCartConsumeAction(
  state: unknown,
): HubEmptyCartConsumeAction {
  if (state === null || typeof state !== 'object') {
    return 'noop';
  }
  const record = state as { readonly openHubEmptyCart?: unknown };
  if (!('openHubEmptyCart' in record)) {
    return 'noop';
  }
  if (record.openHubEmptyCart === true) {
    return 'open_and_clear';
  }
  return 'clear_only';
}

/** True when replace-clear would change serialized location.state. */
export function hubEmptyCartStripWouldChangeState(state: unknown): boolean {
  if (resolveHubEmptyCartConsumeAction(state) === 'noop') {
    return false;
  }
  const stripped = stripHubEmptyCartIntent(state);
  if (stripped === state) {
    return false;
  }
  return JSON.stringify(stripped ?? null) !== JSON.stringify(state ?? null);
}
